import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { CaretRightIcon, CloseIcon, EventIcon, CheckIcon } from '../icons';

type Preset = 'byMonth' | 'last7' | 'all' | 'custom' | undefined;

export interface PeriodSelectorRangeValue {
  from?: Date;
  to?: Date;
  preset: Preset;
}

export interface PeriodSelectorRangeProps {
  value?: PeriodSelectorRangeValue;
  onChange: (value: PeriodSelectorRangeValue) => void;
}

const Container = styled(Box)(({ theme }) => ({
  gap: 8,
  minHeight: 40,
  paddingInline: 12,
  borderRadius: 20,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.primary,
  background: theme.palette.common.white,
  userSelect: 'none',
  cursor: 'pointer',
}));

const formatLabel = (t: any, v: PeriodSelectorRangeValue): string => {
  if (v.preset === 'last7') {
    return t('periodSelectorRange.last7Days', { ns: 'component', defaultValue: 'Last 7 days' });
  }
  if (!v.preset || v.preset === 'all') {
    return t('periodSelectorRange.all', { ns: 'component', defaultValue: 'All' });
  }

  if (v.preset === 'byMonth' && v.from && v.to) {
    return `${dayjs(v.from).format(t('date.short', { ns: 'common', defaultValue: 'DD/MM/YYYY' }))} - ${dayjs(v.to).format(t('date.short', { ns: 'common', defaultValue: 'DD/MM/YYYY' }))}`;
  }
  if (v.preset === 'custom' && v.from && v.to) {
    return `${dayjs(v.from).format(t('date.short', { ns: 'common', defaultValue: 'DD/MM/YYYY' }))} - ${dayjs(v.to).format(t('date.short', { ns: 'common', defaultValue: 'DD/MM/YYYY' }))}`;
  }

  return t('periodSelectorRange.select', { ns: 'component', defaultValue: 'Select range' });
};

const getCurrentMonthRange = (): { from: Date; to: Date } => {
  const start = dayjs().startOf('month');
  const end = dayjs().endOf('month');
  return { from: start.toDate(), to: end.toDate() };
};

const getLast7Range = (): { from: Date; to: Date } => {
  const end = dayjs();
  const start = end.subtract(6, 'day');
  return { from: start.toDate(), to: end.toDate() };
};

const PeriodSelectorRange: React.FC<PeriodSelectorRangeProps> = ({ value, onChange }) => {
  const theme = useTheme();
  const { t } = useTranslation(['common']);
  const [open, setOpen] = useState(false);
  const [openCustom, setOpenCustom] = useState(false);
  const [activeField, setActiveField] = useState<'from' | 'to'>('from');
  const [tempFrom, setTempFrom] = useState<Dayjs | null>(null);
  const [tempTo, setTempTo] = useState<Dayjs | null>(null);

  const [internalValue, setInternalValue] = useState<PeriodSelectorRangeValue>(() => {
    if (value) return value;

    const m = getCurrentMonthRange();
    return { ...m, preset: 'byMonth' };
  });

  const currentValue: PeriodSelectorRangeValue = value || internalValue;

  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (currentValue.preset === 'custom') {
      setTempFrom(currentValue.from ? dayjs(currentValue.from) : dayjs());
      setTempTo(currentValue.to ? dayjs(currentValue.to) : dayjs());
    }
  }, [currentValue]);

  const applyPreset = (preset: Preset) => {
    let newValue: PeriodSelectorRangeValue;

    if (preset === 'byMonth') {
      const m = getCurrentMonthRange();
      newValue = { ...m, preset };
      setInternalValue(newValue);
      onChange(newValue);
      setOpen(false);
      return;
    }

    if (preset === 'last7') {
      const r = getLast7Range();
      newValue = { ...r, preset };
      setInternalValue(newValue);
      onChange(newValue);
      setOpen(false);
      return;
    }

    if (!preset || preset === 'all') {
      newValue = { from: undefined, to: undefined, preset: 'all' };
      setInternalValue(newValue);
      onChange(newValue);
      setOpen(false);
      return;
    }

    if (preset === 'custom') {
      setOpenCustom(true);
    }
  };

  const goPrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    let newValue: PeriodSelectorRangeValue;

    if (currentValue.preset === 'byMonth') {
      const base = dayjs(currentValue.from ?? new Date()).subtract(1, 'month');
      const range = { from: base.startOf('month').toDate(), to: base.endOf('month').toDate() };
      newValue = { ...range, preset: 'byMonth' };
      setInternalValue(newValue);
      onChange(newValue);
    } else if (currentValue.preset === 'last7') {
      const end = dayjs((currentValue.to ?? new Date())).subtract(7, 'day');
      const start = end.subtract(6, 'day');
      newValue = { from: start.toDate(), to: end.toDate(), preset: 'last7' };
      setInternalValue(newValue);
      onChange(newValue);
    }
  };

  const goNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    let newValue: PeriodSelectorRangeValue;

    if (currentValue.preset === 'byMonth') {
      const base = dayjs(currentValue.from ?? new Date()).add(1, 'month');
      const range = { from: base.startOf('month').toDate(), to: base.endOf('month').toDate() };
      newValue = { ...range, preset: 'byMonth' };
      setInternalValue(newValue);
      onChange(newValue);
    } else if (currentValue.preset === 'last7') {
      const end = dayjs((currentValue.to ?? new Date())).add(7, 'day');
      const start = end.subtract(6, 'day');
      newValue = { from: start.toDate(), to: end.toDate(), preset: 'last7' };
      setInternalValue(newValue);
      onChange(newValue);
    }
  };

  const handleCustomPick = (picked: Dayjs | null) => {
    if (!picked) return;
    if (activeField === 'from') {
      setTempFrom(picked);
      setActiveField('to');
    } else {
      setTempTo(picked);
    }
  };

  const confirmCustom = () => {
    if (!tempFrom || !tempTo) return;
    const from = tempFrom.isBefore(tempTo) ? tempFrom.toDate() : tempTo.toDate();
    const to = tempFrom.isBefore(tempTo) ? tempTo.toDate() : tempFrom.toDate();
    const newValue = { from, to, preset: 'custom' as Preset };
    setInternalValue(newValue);
    onChange(newValue);
    setOpen(false);
    setOpenCustom(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container onClick={() => setOpen(true)} role="button" aria-label="open-period-selector">
        {currentValue.preset === 'byMonth' && <IconButton size="small" onClick={goPrev} aria-label="previous" sx={{ color: 'inherit' }}>
          <CaretRightIcon style={{ fontSize: 20, transform: 'rotate(180deg)' }} />
        </IconButton>}
        <Stack direction="row" alignItems="center" justifyContent="center" gap={1} sx={{ flex: 1 }}>
          <EventIcon style={{ fontSize: 18, color: 'currentColor' }} />
          <Typography fontWeight={500} sx={{ color: 'inherit' }}>
            {formatLabel(t, currentValue)}
          </Typography>
        </Stack>
        {currentValue.preset === 'byMonth' && <IconButton size="small" onClick={goNext} aria-label="next" sx={{ color: 'inherit' }}>
          <CaretRightIcon style={{ fontSize: 20 }} />
        </IconButton>}
      </Container>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          zIndex: theme.zIndex.modal + 10,
          '& .MuiDrawer-paper': { borderTopLeftRadius: 20, borderTopRightRadius: 20 },
        }}
      >
        {!openCustom && (
          <Box>
            <Box textAlign="center" paddingInline={2} paddingTop={2}>
              <IconButton size="large" onClick={() => setOpen(false)} style={{ position: 'absolute', left: 4, top: 4 }}>
                <CloseIcon />
              </IconButton>
              <Typography variant="h2">{t('periodSelectorRange.title', { ns: 'component', defaultValue: 'Filter time' })}</Typography>
            </Box>
            <List>
              {([
                { key: 'byMonth', label: t('periodSelectorRange.byMonth', { ns: 'component', defaultValue: 'By month' }) },
                { key: 'last7', label: t('periodSelectorRange.last7Days', { ns: 'component', defaultValue: 'Last 7 days' }) },
                { key: 'all', label: t('periodSelectorRange.all', { ns: 'component', defaultValue: 'All' }) },
                { key: 'custom', label: t('periodSelectorRange.custom', { ns: 'component', defaultValue: 'Custom' }) },
              ] as { key: Preset; label: string }[]).map((item) => {
                const isActive = currentValue.preset === item.key || ((!currentValue.preset || currentValue.preset === 'all') && item.key === 'all');

                return (
                  <ListItemButton
                    key={item.key}
                    selected={isActive}
                    onClick={() => applyPreset(item.key)}
                    sx={(theme) => ({
                      borderRadius: 0,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.blue[300],
                        color: theme.palette.common.white,
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: theme.palette.blue[300],
                      },
                    })}
                  >
                    <ListItemText primary={item.label} />
                    {isActive && <CheckIcon style={{ color: 'white' }} />}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}

        {openCustom && (
          <Paper sx={{ borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }}>
            <Container>
              <Stack useFlexGap spacing={2} color={theme.palette.text.primary}>
                <Box textAlign="center" paddingInline={2} paddingTop={2}>
                  <IconButton
                    size="large"
                    onClick={() => setOpenCustom(false)}
                    style={{ position: 'absolute', left: 4, top: 4, color: 'inherit' }}
                    aria-label="close-custom-range"
                  >
                    <CloseIcon />
                  </IconButton>
                  <IconButton
                    size="large"
                    onClick={confirmCustom}
                    style={{ position: 'absolute', right: 4, top: 4, color: !tempFrom || !tempTo ? theme.palette.text.disabled : 'inherit' }}
                    aria-label="confirm-custom-range"
                    disabled={!tempFrom || !tempTo}
                  >
                    <CheckIcon />
                  </IconButton>
                  <Typography variant="h2">{t('periodSelectorRange.chooseRange', { ns: 'component', defaultValue: 'Choose range' })}</Typography>
                </Box>

                <Box py={2} px={3} borderRadius={2} bgcolor={theme.palette.navy[100]} >
                  <Stack
                    useFlexGap
                    gap={2}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="h4">{t('from', { ns: 'common', defaultValue: 'Từ' })}:</Typography>
                      <Button
                        disableElevation
                        size="small"
                        onClick={() => setActiveField('from')}
                        variant={activeField === 'from' ? "contained" : "outlined"}
                        sx={{
                          minWidth: 120,
                          textAlign: 'center',
                          paddingLine: theme.spacing(4),
                        }}>
                        <ListItemText
                          primary={tempFrom ? dayjs(tempFrom).format(t('date.short', { ns: 'common' })) : '—'}
                        />
                      </Button>
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="h4">{t('to', { ns: 'common', defaultValue: 'Đến' })}:</Typography>
                      <Button
                        disableElevation
                        size="small"
                        variant={activeField === 'to' ? "contained" : "outlined"}
                        onClick={() => setActiveField('to')}
                        sx={{
                          minWidth: 120,
                          textAlign: 'center',
                          paddingLine: theme.spacing(4),
                        }}
                      >
                        <ListItemText primary={tempTo ? dayjs(tempTo).format(t('date.short', { ns: 'common' })) : '—'} />
                      </Button>
                    </Stack>
                  </Stack>
                </Box>


                <Card
                  elevation={4}
                  sx={{
                    backgroundColor: theme.palette.navy[100],
                    marginBottom: theme.spacing(2),
                  }}
                >
                  <StaticDatePicker
                    value={activeField === 'from' ? tempFrom : tempTo}
                    onChange={handleCustomPick}
                    slotProps={{
                      actionBar: { actions: [] },
                      toolbar: { hidden: true },
                    }}
                    sx={{
                      backgroundColor: theme.palette.navy[100],
                      width: '100%',
                      '& .MuiDateCalendar-root': { height: 'auto' }
                    }}
                  />
                </Card>
              </Stack>
            </Container>
          </Paper>
        )}
      </Drawer>
    </LocalizationProvider>
  );
};

export default PeriodSelectorRange;


